import { createColumnHelper } from '@tanstack/react-table';
import {
  InternalApiLibraryScenario,
  InternalApiScenarioBasicDetails,
  InternalApiScenarioMetric,
  InternalApiScenarioStatus,
  InternalApiScenarioTypeV2,
} from '../api-internal';
import { assignParameterValues } from '../common/versioning/helper';
import { SalvLabel } from '@salvapp/ui-components';
import dayjs from 'dayjs';
import { FULL_DATE_FORMAT } from '../common/helper';
import { statusSwitchers } from './MonitoringScenario';
import React from 'react';
import { monitoringScenarioTypes } from './MonitoringScenarios';

export const getScenarioColumns = () => {
  const columnHelper = createColumnHelper<InternalApiScenarioBasicDetails>();

  return [
    columnHelper.accessor('internalId', {
      header: 'Internal ID',
      cell: ({ getValue }) => <span className='text-uppercase'>{getValue() || '-'}</span>,
    }),
    columnHelper.accessor('name', {
      header: 'Scenario Name',
      cell: ({ getValue }) => <span className={'text-capitalize'}>{getValue().toLowerCase()}</span>,
      size: 40,
    }),
    columnHelper.accessor('currentVersionLogic', {
      header: 'Current version logic',
      cell: ({ row }) =>
        row.original.currentVersionLogic && row.original.currentVersionParameters
          ? assignParameterValues(row.original.currentVersionLogic, row.original.currentVersionParameters)
          : '-',
    }),

    columnHelper.accessor('type', {
      header: 'Type',
      cell: ({ row }) => (
        <div style={{ minWidth: '10ch' }}>
          <div>{monitoringScenarioTypes[row.original.type as InternalApiScenarioTypeV2]}</div>
          <div> {!row.original.libraryScenarioHandle && <SalvLabel variant='secondary'>Custom</SalvLabel>}</div>
        </div>
      ),
    }),

    columnHelper.display({
      header: 'Category & Typology',
      cell: ({ row }) => {
        return (
          <div>
            <div className='m-r-4'>{row.original.category?.name || '-'}</div>
            <div className='text-secondary'>{row.original.typology?.name}</div>
          </div>
        );
      },
    }),
    columnHelper.accessor('updatedTime', {
      id: 'updatedTime',
      header: 'Last updated',
      cell: ({ getValue }) => (
        <>
          {dayjs(getValue()).format(FULL_DATE_FORMAT)}
          <span className='text-xs text-secondary m-l-4'>{dayjs(getValue()).format('HH:mm')}</span>
        </>
      ),
      enableSorting: true,
    }),
    columnHelper.accessor('status', {
      id: 'status',
      header: 'Status',
      cell: ({ getValue }) => statusSwitchers(getValue() as InternalApiScenarioStatus, false, false).label,
      enableSorting: true,
    }),
  ];
};

export const getScenarioMetricColumns = () => {
  const columnHelper = createColumnHelper<InternalApiScenarioMetric>();

  return [
    columnHelper.accessor('scenarioName', {
      header: '',
      cell: ({ getValue, row }) => (
        <a className='text-xs text-capitalize' href={`/scenarios/${row.original.scenarioHandle}`}>
          {getValue().toLowerCase()}
        </a>
      ),
    }),
    columnHelper.accessor('scenarioType', {
      header: '',
      cell: ({ row }) => <div className='text-xs'>{monitoringScenarioTypes[row.original.scenarioType]}</div>,
    }),
    columnHelper.accessor('alertCountPercentage', {
      header: '',
      cell: ({ getValue }) => <span className='text-xs'>{getValue()}%</span>,
    }),
  ];
};

export const getLibraryScenarioColumns = () => {
  const libraryColumnHelper = createColumnHelper<InternalApiLibraryScenario>();

  return [
    libraryColumnHelper.accessor('internalId', {
      header: 'Internal ID',
      cell: ({ getValue }) => <span className='text-uppercase'>{getValue() || '-'}</span>,
    }),
    libraryColumnHelper.accessor('name', {
      header: 'Scenario Name',
      cell: ({ getValue }) => <span className={'text-capitalize'}>{getValue().toLowerCase()}</span>,
      size: 40,
    }),
    libraryColumnHelper.accessor('logic', {
      header: 'Logic',
      cell: ({ row }) =>
        row.original.logic && row.original.parameters
          ? assignParameterValues(row.original.logic, row.original.parameters)
          : '-',
    }),

    libraryColumnHelper.accessor('type', {
      header: 'Type',
      cell: ({ row }) =>
        row.original.type.map((type) => (
          <div key={`$ {row.original.id}-${type}`} style={{ minWidth: '10ch' }}>
            <div>
              <SalvLabel variant='secondary' className='m-b-8 m-r-8'>
                {monitoringScenarioTypes[type as InternalApiScenarioTypeV2]}
              </SalvLabel>
            </div>
          </div>
        )),
    }),

    libraryColumnHelper.accessor('categories', {
      header: 'Category & Typology',
      cell: ({ row }) =>
        row.original.categories.map((category) => (
          <div key={category.id}>
            <div className='m-r-4'>{category.name || '-'}</div>
            <div className='text-secondary'>{category.typologies.map((typo) => typo.name).join(', ')}</div>
          </div>
        )),
    }),

    libraryColumnHelper.accessor('status', {
      id: 'status',
      header: 'Status',
      cell: ({ getValue }) => statusSwitchers(getValue() as InternalApiScenarioStatus, false, false).label,
    }),
  ];
};
